import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { Camera, X, ScanLine } from "lucide-react";

/**
 * Camera-based barcode/QR scanner.
 * Opens a modal with the device camera and emits the decoded code via onResult.
 */
export default function BarcodeScanner({ onResult, label = "Scan", className = "btn-ghost" }) {
  const [open, setOpen] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [selectedCamId, setSelectedCamId] = useState("");
  const [error, setError] = useState("");
  const scannerRef = useRef(null);
  const containerId = useRef(`qr-region-${Math.random().toString(36).slice(2)}`);

  // Discover cameras when opened
  useEffect(() => {
    if (!open) return;
    setError("");
    Html5Qrcode.getCameras()
      .then((cams) => {
        setCameras(cams);
        if (cams.length > 0) {
          // prefer back camera
          const back = cams.find((c) => /back|rear|environment/i.test(c.label));
          setSelectedCamId(back ? back.id : cams[0].id);
        } else {
          setError("No camera found on this device.");
        }
      })
      .catch((e) => setError(e?.message || "Camera access denied. Please allow camera permission."));
  }, [open]);

  // Start scanner once camera selected
  useEffect(() => {
    if (!open || !selectedCamId) return;
    let stopped = false;
    const html5 = new Html5Qrcode(containerId.current, {
      formatsToSupport: [
        Html5QrcodeSupportedFormats.QR_CODE,
        Html5QrcodeSupportedFormats.EAN_13,
        Html5QrcodeSupportedFormats.EAN_8,
        Html5QrcodeSupportedFormats.UPC_A,
        Html5QrcodeSupportedFormats.UPC_E,
        Html5QrcodeSupportedFormats.CODE_128,
        Html5QrcodeSupportedFormats.CODE_39,
        Html5QrcodeSupportedFormats.CODE_93,
        Html5QrcodeSupportedFormats.ITF,
        Html5QrcodeSupportedFormats.DATA_MATRIX,
      ],
    });
    scannerRef.current = html5;

    const config = {
      fps: 12,
      qrbox: (vw, vh) => {
        const min = Math.min(vw, vh);
        const w = Math.floor(min * 0.85);
        return { width: w, height: Math.floor(w * 0.55) };
      },
      aspectRatio: 1.5,
    };

    html5.start(selectedCamId, config, (decodedText) => {
      if (stopped) return;
      stopped = true;
      // small haptic-like feedback via beep using web audio
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const o = ctx.createOscillator();
        o.frequency.value = 880; o.connect(ctx.destination); o.start();
        setTimeout(() => { o.stop(); ctx.close(); }, 90);
      } catch {}
      html5.stop().then(() => html5.clear()).finally(() => {
        setOpen(false);
        onResult?.(decodedText);
      });
    }).catch((e) => setError(e?.message || "Unable to start camera."));

    return () => {
      stopped = true;
      if (html5 && html5.isScanning) {
        html5.stop().then(() => html5.clear()).catch(() => {});
      }
    };
  }, [open, selectedCamId, onResult]);

  const close = () => {
    if (scannerRef.current && scannerRef.current.isScanning) {
      scannerRef.current.stop().then(() => scannerRef.current.clear()).finally(() => setOpen(false));
    } else {
      setOpen(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={className} data-testid="open-scanner-btn">
        <Camera className="w-4 h-4" /> {label}
      </button>
      {open && (
        <div className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="scanner-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ScanLine className="w-4 h-4 text-[#002FA7]" />
                <h3 className="font-display font-semibold">Scan barcode / QR</h3>
              </div>
              <button onClick={close} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3">
              {cameras.length > 1 && (
                <select
                  value={selectedCamId}
                  onChange={(e) => setSelectedCamId(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                >
                  {cameras.map((c) => <option key={c.id} value={c.id}>{c.label || c.id.slice(0, 8)}</option>)}
                </select>
              )}
              {error ? (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
                  {error}
                  <div className="text-xs text-red-500 mt-2">
                    Tip: this preview must be opened over HTTPS for camera access. Granted permission once? Try refreshing.
                  </div>
                </div>
              ) : (
                <div id={containerId.current} className="rounded-lg overflow-hidden bg-black aspect-[3/2]" />
              )}
              <div className="text-xs text-slate-500 text-center">
                Hold the barcode steady inside the frame. We'll capture and close automatically.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
